import { Button } from "@/components/ui/baseUi/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/baseUi/dialog"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { BoxStaffCoachIcon } from "@images/svgIcons/boxStaff/boxStaffCoachIcon"
import { BoxStaffCoOwnerIcon } from "@images/svgIcons/boxStaff/boxStaffCoOwnerIcon"
import { BoxStaffHeadCoachIcon } from "@images/svgIcons/boxStaff/boxStaffHeadCoachIcon"
import { ForbiddenIcon } from "@images/svgIcons/forbiddenIcon"
import { BoxStaffDetailedEntity } from "@modules/boxStaff/entities/boxStaffEntity"
import { BoxStaffRole } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffActions } from "@modules/boxStaff/hooks/useBoxStaff"
import { word } from "@modules/core/utils/i18n"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { useCallback, useImperativeHandle, useState } from "react"

export type BoxStaffRoleModalRef = {
  open: () => void
} & React.ComponentProps<"div">

export type BoxStaffRoleModalProps = {
  staffDetailed: BoxStaffDetailedEntity
  ref: React.Ref<BoxStaffRoleModalRef>
  className?: string
}

export function BoxStaffRoleModal({ staffDetailed, ref, className, ...props }: BoxStaffRoleModalProps) {
  const [showModal, setShowModal] = useState(false)
  const [role, setRole] = useState<BoxStaffRole | undefined>(staffDetailed.role)
  const { hideSidebar } = useSidebar()
  const { updateBoxStaff, deleteBoxStaff, isPending } = useBoxStaffActions(staffDetailed.id)

  const changeRole = (role?: BoxStaffRole): void => {
    setRole(role)
  }

  const onHide = useCallback(() => {
    setShowModal(false)
  }, [])

  useImperativeHandle(ref, () => ({
    open: () => {
      setShowModal(true)
    },
  }))

  async function handleOnSubmit(): Promise<void> {
    if (role) {
      await updateBoxStaff({ role })
        .then(() => {
          toast.success(word("box.staff.roleModal.toast.update.success"), {
            classNames: successToastClassName(),
          })
        })
        .catch(() => {
          toast.error(word("box.staff.roleModal.toast.update.error"), {
            classNames: errorToastClassName(),
          })
        })
    } else if (role === undefined) {
      await deleteBoxStaff()
        .then(() => {
          toast.success(word("box.staff.roleModal.toast.delete.success"), {
            classNames: successToastClassName(),
          })
          hideSidebar()
        })
        .catch(() => {
          toast.error(word("box.staff.roleModal.toast.delete.error"), {
            classNames: errorToastClassName(),
          })
        })
    }
    onHide()
  }

  return (
    <Dialog open={showModal} onOpenChange={onHide}>
      <DialogContent className={cn("w-120", className)} aria-describedby={undefined} {...props}>
        <DialogHeader>
          <DialogTitle>
            <div className="flex flex-col flex-wrap content-center justify-center gap-2 text-center">
              <Text fontStyle="TL" className="text-textDefault-heading">
                {word("box.staff.roleModal.label.header")}
              </Text>
              <Text fontStyle="BM" className="text-textDefault-caption">
                {word("box.staff.roleModal.label.subHeader")}
              </Text>
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* ROLE */}
        <div className="mt-6 flex flex-col gap-2">
          <div
            className={`flex items-center gap-2 rounded-xl border px-3 py-2 ${
              role === BoxStaffRole.COACH
                ? "border-borderPrimary"
                : "cursor-pointer border-borderDefault hover:bg-surface-card-hover"
            }`}
            onClick={() => changeRole(BoxStaffRole.COACH)}
          >
            <BoxStaffCoachIcon className="text-textDefault-body" size={50} color="currentColor" />
            <Text fontStyle="LL" className="text-textDefault-body">
              {word("box.staff.roleModal.global.coach")}
            </Text>
          </div>
          <div
            className={`flex items-center gap-2 rounded-xl border px-3 py-2 ${
              role === BoxStaffRole.HEAD_COACH
                ? "border-borderPrimary"
                : "cursor-pointer border-borderDefault hover:bg-surface-card-hover"
            }`}
            onClick={() => changeRole(BoxStaffRole.HEAD_COACH)}
          >
            <BoxStaffHeadCoachIcon className="text-textDefault-body" size={50} color="currentColor" />
            <Text fontStyle="LL" className="text-textDefault-body">
              {word("box.staff.roleModal.global.headCoach")}
            </Text>
          </div>
          <div
            className={`flex items-center gap-2 rounded-xl border px-3 py-2 ${
              role === BoxStaffRole.CO_OWNER
                ? "border-borderPrimary"
                : "cursor-pointer border-borderDefault hover:bg-surface-card-hover"
            }`}
            onClick={() => changeRole(BoxStaffRole.CO_OWNER)}
          >
            <BoxStaffCoOwnerIcon className="text-textDefault-body" size={50} color="currentColor" />
            <Text fontStyle="LL" className="text-textDefault-body">
              {word("box.staff.roleModal.global.coOwner")}
            </Text>
          </div>
          <div
            className={`flex items-center gap-2 rounded-xl border px-3 py-2 ${
              role === undefined
                ? "border-borderPrimary"
                : "cursor-pointer border-borderDefault hover:bg-surface-card-hover"
            }`}
            onClick={() => changeRole()}
          >
            <ForbiddenIcon className="text-primary-300" width={50} height={50} color="currentColor" />
            <div className="flex flex-col">
              <Text fontStyle="LL" className="text-textDefault-body">
                {word("box.staff.roleModal.global.noRole")}
              </Text>
              <Text fontStyle="BS" className="text-textDefault-body">
                ({word("box.staff.roleModal.global.noRoleSub")})
              </Text>
            </div>
          </div>
        </div>
        {/* BUTTON */}
        <div className="mt-6 mb-6 flex w-full flex-row items-center justify-center gap-2">
          <Button variant="outline" size="lg" onClick={() => onHide()} className="grow">
            {word("global.cancel")}
          </Button>
          <Button
            variant="primary"
            size="lg"
            onClick={() => void (staffDetailed.role !== role && handleOnSubmit())}
            disabled={staffDetailed.role === role || isPending}
            className="grow"
          >
            {word("global.check")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
